/**
 * Watchlist Computation Engine
 *
 * All functions are pure: input → output, no side effects.
 * Handles null/missing inputs gracefully (returns null or "—").
 */

import type { PriceData } from '@/types'

export interface SMAData {
  ticker: string
  sma_20: number | null
  sma_50: number | null
  sma_200: number | null
  parkinsonIV: number | null
  ivPremiumLevel: 'Low' | 'Medium' | 'High' | null
  fetchedAt: number
}

export interface ComputedColumns {
  compression: number | null
  bestStrategy: string
  wheelQuality: string
  leapCallQuality: string
  leapPutQuality: string
  assignmentComfort: string
  riskLevel: string
  technicalStatus: 'Extended' | 'Breakout' | 'Neutral' | 'Support' | 'Pullback' | null
  idealEntryZone: string | null
  gainPotential: string
  recommendationStatus: 'Immediate Candidate' | 'Research' | 'Wait'
}

/**
 * Compression: how far below 52-week high (as positive percentage).
 * e.g. price=150, high=200 → 25%
 */
export function computeCompression(
  currentPrice: number | null | undefined,
  high52w: number | null | undefined
): number | null {
  if (!currentPrice || !high52w || high52w === 0) return null
  return ((high52w - currentPrice) / high52w) * 100
}

/**
 * Best strategy recommendation based on 3-year ownership conviction score.
 */
export function computeBestStrategy(ownScore: number): string {
  if (ownScore >= 8) return 'Wheel / Aggressive CSP'
  if (ownScore >= 5) return 'LEAP / Covered Call'
  return 'Avoid'
}

/**
 * Wheel quality based on conviction score + priority tier.
 */
export function computeWheelQuality(ownScore: number, tier: number): string {
  if (ownScore >= 8 && tier === 1) return 'Excellent'
  if (ownScore >= 8 && tier === 2) return 'Good'
  if (ownScore >= 5 && tier === 1) return 'Fair'
  return 'Poor'
}

/**
 * LEAP Call quality based on conviction + tier.
 */
export function computeLeapCallQuality(ownScore: number, tier: number): string {
  if (ownScore >= 7 && tier <= 2) return 'Excellent'
  if (ownScore >= 5) return 'Good'
  return 'Poor'
}

/**
 * LEAP Put quality based on conviction + hedge candidate flag.
 */
export function computeLeapPutQuality(ownScore: number, hedgeCandidate: boolean): string {
  if (ownScore >= 6 && hedgeCandidate) return 'Excellent'
  if (ownScore >= 6) return 'Good'
  return 'N/A'
}

/**
 * Assignment comfort level (how comfortable owning shares if assigned).
 */
export function computeAssignmentComfort(ownScore: number): string {
  if (ownScore >= 8) return 'Very Comfortable'
  if (ownScore >= 5) return 'Comfortable'
  return 'Uncomfortable'
}

/**
 * Risk level (inverse of conviction).
 */
export function computeRiskLevel(ownScore: number): string {
  if (ownScore >= 8) return 'Low'
  if (ownScore >= 5) return 'Medium'
  return 'High'
}

/**
 * Technical status relative to 50-day SMA.
 * >+5% = Extended, +1-5% = Breakout, ±1% = Neutral, -1 to -5% = Support, <-5% = Pullback
 */
export function computeTechnicalStatus(
  currentPrice: number | null | undefined,
  sma50: number | null | undefined
): 'Extended' | 'Breakout' | 'Neutral' | 'Support' | 'Pullback' | null {
  if (!currentPrice || !sma50 || sma50 === 0) return null
  const pctFromSMA = ((currentPrice - sma50) / sma50) * 100
  if (pctFromSMA > 5) return 'Extended'
  if (pctFromSMA > 1) return 'Breakout'
  if (pctFromSMA >= -1) return 'Neutral'
  if (pctFromSMA >= -5) return 'Support'
  return 'Pullback'
}

/**
 * Ideal entry zone — the SMA level representing max entry ceiling for selling puts.
 */
export function computeIdealEntryZone(
  currentPrice: number | null | undefined,
  sma20: number | null | undefined,
  sma50: number | null | undefined,
  sma200: number | null | undefined
): string | null {
  if (!currentPrice) return null
  if (sma20 && currentPrice > sma20) return `20 SMA ($${sma20.toFixed(2)})`
  if (sma50 && currentPrice > sma50) return `50 SMA ($${sma50.toFixed(2)})`
  if (sma200) return `200 SMA ($${sma200.toFixed(2)})`
  return null
}

/**
 * Gain potential based on compression from 52W high.
 */
export function computeGainPotential(compression: number | null): string {
  if (compression === null || compression === undefined) return '—'
  if (compression > 35) return 'High Likelihood'
  if (compression > 18) return 'Medium Likelihood'
  return 'Low Likelihood'
}

/**
 * Recommendation status — final buy/wait signal.
 */
export function computeRecommendationStatus(
  technicalStatus: string | null | undefined,
  ivPremium: string | null | undefined
): 'Immediate Candidate' | 'Research' | 'Wait' {
  if (technicalStatus === 'Pullback' && ivPremium === 'High') return 'Immediate Candidate'
  if (technicalStatus === 'Support' || technicalStatus === 'Pullback') return 'Research'
  return 'Wait'
}

/**
 * Aggregator: compute all derived columns for a single watchlist row.
 */
export function computeAllForRow(
  row: { own_score_3y: number; priority_tier: number; hedge_candidate: boolean; iv_premium_level?: string | null },
  price: PriceData | null | undefined,
  sma: SMAData | null | undefined
): ComputedColumns {
  const currentPrice = price?.currentPrice ?? null
  const high52w = price?.fiftyTwoWeekHigh ?? null

  const compression = computeCompression(currentPrice, high52w)
  const technicalStatus = computeTechnicalStatus(currentPrice, sma?.sma_50 ?? null)
  const ivPremium = sma?.ivPremiumLevel ?? row.iv_premium_level ?? null

  return {
    compression,
    bestStrategy: computeBestStrategy(row.own_score_3y),
    wheelQuality: computeWheelQuality(row.own_score_3y, row.priority_tier),
    leapCallQuality: computeLeapCallQuality(row.own_score_3y, row.priority_tier),
    leapPutQuality: computeLeapPutQuality(row.own_score_3y, row.hedge_candidate),
    assignmentComfort: computeAssignmentComfort(row.own_score_3y),
    riskLevel: computeRiskLevel(row.own_score_3y),
    technicalStatus,
    idealEntryZone: computeIdealEntryZone(currentPrice, sma?.sma_20 ?? null, sma?.sma_50 ?? null, sma?.sma_200 ?? null),
    gainPotential: computeGainPotential(compression),
    recommendationStatus: computeRecommendationStatus(technicalStatus, ivPremium),
  }
}
